import { useContext, useEffect, useRef, useState } from 'react';
import styles from './AlertLog.module.css';
import AIEduDataTable from '../../components/DataTable/AIEduDataTable';
import AIEduSearch from '../../components/Search/AIEduSearch';
import AIEduPaginator from '../../components/Paginator/AIEduPaginator';
import AIEduSpinner from '../../components/Spinner/AIEduSpinner';
import AIEduToast from '../../components/Toast/AIEduToast';
import { API } from '../../services';
import { useAuthenticate } from '../../hooks';
import { Context } from '../../context/Context';
import { setToastComponent } from '../../services/setup/ApiResponseHandler';
import { useIntl } from 'react-intl';
import { formatDateWithTimeStamp } from '../../utils/Utils';
import { ASC, DESC } from '../../utils/Constants';
import PageTitle from '../../layout/PageTitle/pageTitle';

const AlertLog = () => {
    const intl = useIntl();
    const { formatMessage: f } = intl;

    const { accountInfo } = useAuthenticate();
    const { organizations, place } = accountInfo ?? {};
    const { placeId } = place ?? {};
    const { organizationId } = organizations?.[0] ?? {};

    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const { refreshMap } = useContext(Context);

    const [searchTerm, setSearchTerm] = useState('');
    const toastRef = useRef(null);
    const [alertLogs, setAlertLogs] = useState([]);

    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState(-1);

    const onPageChange = (e) => {
        const page = e.page;
        const size = e.rows;

        setFirst(e.first);
        setRows(size);
        setPage(page);

        getAllAlertLogsDetails(page, size, sortField, sortOrder, searchTerm);
    };

    const getAllAlertLogsDetails = (
        start,
        rowsPerPage,
        sortBy,
        sort,
        search
    ) => {
        setLoading(true);
        const params = {
            start,
            rowsPerPage,
            sortBy: sortBy || 'createdAt',
            sort: sort === 1 ? ASC : DESC,
            search,
        };
        API.getAllAlertLogs(organizationId, params)
            .then(({ data }) => {
                const { alertLogs, pagination, sorting } = data.data ?? {};
                const { rowsPerPage, total } = pagination ?? {};
                const { sortBy } = sorting ?? {};
                if (alertLogs?.length === 0 && start > 0 && total > 0) {
                    setFirst(0);
                    setPage(0);
                    getAllAlertLogsDetails(
                        0,
                        rowsPerPage,
                        sortBy,
                        sortOrder,
                        search
                    );
                } else {
                    const alertLogList = alertLogs?.map((alertLog) => {
                        const {
                            logId,
                            title,
                            description,
                            alertType,
                            createdAt,
                        } = alertLog ?? {};
                        return {
                            logId,
                            title,
                            description,
                            alertType,
                            createdAt,
                        };
                    });
                    setAlertLogs(alertLogList);
                    setTotalRecords(total);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const resetPageParams = () => {
        setFirst(0);
        setPage(0);
        setSortField('createdAt');
        setSortOrder(-1);
        setSearchTerm('');
    };

    useEffect(() => {
        setToastComponent(toastRef.current);
    }, []);

    useEffect(() => {
        resetPageParams();
        getAllAlertLogsDetails(0, rows);
    }, [refreshMap.alertLog]);

    const createdTimeTemplate = (rowData) => {
        return !!rowData.createdAt
            ? formatDateWithTimeStamp(rowData.createdAt)
            : '--';
    };

    const columns = [
        {
            field: 'title',
            header: f({ id: 'AI_EDU_ALERT_TITLE' }),
            width: '25%',
            minWidth: '15rem',
            sortable: true,
        },
        {
            field: 'alertType',
            header: f({ id: 'AI_EDU_ALERT_TYPE' }),
            width: '20%',
            minWidth: '10rem',
            sortable: true,
        },
        {
            field: 'description',
            header: f({ id: 'AI_EDU_ALERT_DESCRIPTION' }),
            width: '40%',
            minWidth: '25rem',
        },
        {
            field: 'createdAt',
            header: f({ id: 'AI_EDU_CREATED_DATE' }),
            body: createdTimeTemplate,
            minWidth: '10rem',
            sortable: true,
        },
    ];

    const handleAlertLogSearch = () => {
        setFirst(0);
        setPage(0);
        getAllAlertLogsDetails(0, rows, sortField, sortOrder, searchTerm);
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleAlertLogSearch();
        }
    };

    const onSort = (e) => {
        const { field, order } = { field: e.sortField, order: e.sortOrder };

        getAllAlertLogsDetails(
            page,
            rows,
            field,
            order,
            !!searchTerm ? searchTerm : null
        );

        setSortField(field);
        setSortOrder(order);
    };

    const sortIcon = (options) => {
        if (options.sortOrder === 1) {
            return (
                <i
                    className='pi pi-sort-up-fill'
                    style={{ marginLeft: '0.5rem', marginTop: '0.2rem' }}
                />
            );
        }
        if (options.sortOrder === -1) {
            return (
                <i
                    className='pi pi-sort-down-fill'
                    style={{ marginLeft: '0.5rem', marginTop: '0.2rem' }}
                />
            );
        }
    };

    return (
        <>
            {loading && <AIEduSpinner />}
            <AIEduToast ref={toastRef} />
            <div className={styles.alert_log}>
                <PageTitle
                    title={f({ id: 'AI_EDU_ALERT_LOG' })}
                    count={totalRecords}
                />
                <div className='mb-6 flex justify-content-between flex-wrap gap-3'>
                    <AIEduSearch
                        value={searchTerm}
                        setValue={setSearchTerm}
                        handleInputKeyDown={handleInputKeyDown}
                        placeholder={f({ id: 'AI_EDU_SEARCH_ALERT' })}
                        searchHandler={handleAlertLogSearch}
                    />
                </div>
                <AIEduDataTable
                    data={alertLogs}
                    columns={columns}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    sortIcon={sortIcon}
                    onSort={onSort}
                    lazy={true}
                    dataKey='logId'
                    scrollHeight='60vh'
                    emptyMessage={f({ id: 'AI_EDU_NO_DATA_TO_SHOW' })}
                />

                {alertLogs?.length > 0 && (
                    <AIEduPaginator
                        rows={rows}
                        first={first}
                        totalRecords={totalRecords}
                        onPageChange={onPageChange}
                    />
                )}
            </div>
        </>
    );
};

export default AlertLog;
